import {
    useAppDispatch,
    useAppSelector,
} from 'hooks'
import {
    useEffect,
    useMemo,
} from 'react'
import {NinchatRealmsStore} from 'api/types/store'
import {RealmID} from 'api/types/properties'
import {describeRealm} from 'api/action/describeRealm'

export const useRealms = (realmIds: (RealmID | undefined)[] = [], fetchComplete = false): NinchatRealmsStore => {
    const _realms = useAppSelector(({ninchat}) => ninchat._realms)
    const user_realms = useAppSelector(({ninchat}) => ninchat.user_realms)
    const user_channels = useAppSelector(({ninchat}) => ninchat.user_channels,
        (next, prev) => Object.keys(next).length === Object.keys(prev).length)

    const dispatch = useAppDispatch()

    const processRealmIds = new Set<RealmID>()
    const describeRealmIds = new Set<RealmID>()

    const realms = useMemo(
        () => Object.fromEntries(Object.entries({
            ..._realms,
            ...user_realms,
        }).filter(([realm_id]) => (realmIds.length ? realmIds.includes(realm_id) : true))),
        [JSON.stringify(realmIds), _realms, user_realms],
    )

    const process = (realm_id: RealmID | undefined) => {
        if (realm_id) {
            const _describe = realms[realm_id]?._describe
            const realm_attrs = realms[realm_id]?.realm_attrs
            const shouldDescribe = !_describe && (fetchComplete || !realm_attrs) && !describeRealmIds.has(realm_id)
            if (shouldDescribe) {
                describeRealmIds.add(realm_id)
            }
        }
    }

    useEffect(() => {
        for (const channel of Object.values(user_channels)) {
            const {realm_id} = channel
            if (realm_id) {
                if (!realmIds.length || realmIds.includes(realm_id)) {
                    processRealmIds.add(realm_id)
                }
            }
        }
    }, [user_channels])

    useEffect(() => {
        for (const realm_id of Object.keys(realms)) {
            processRealmIds.add(realm_id)
        }
    }, [realms])

    useEffect(() => {
        for (const realm_id of processRealmIds) {
            process(realm_id)
            processRealmIds.delete(realm_id)
        }

        for (const realm_id of describeRealmIds) {
            dispatch(describeRealm({realm_id}))
            describeRealmIds.delete(realm_id)
        }
    })

    return useMemo(() => Object.fromEntries(Object.entries(realms).filter(([_, realm]) => !!realm.realm_attrs)), [realms])
}
